import { useSyncExternalStore } from 'react';
import { readStorage, writeStorage } from './storage';

/** Appearance › Theme. "dark-sidebar" is a light workspace with a dark navigation rail. */
export type ThemePreference = 'system' | 'light' | 'dark-sidebar' | 'dark';
export type ResolvedTheme = 'light' | 'dark';
/** Appearance › Ambient glow: the hue of the wash across the top of the window. */
export type GlowChoice = 'teal' | 'blue' | 'indigo' | 'violet' | 'rose' | 'amber' | 'off';

export const THEME_OPTIONS: readonly { value: ThemePreference; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark-sidebar', label: 'Dark sidebar' },
  { value: 'dark', label: 'Dark' },
];

/** `swatch` is the token the chooser paints; the glow itself is driven by `data-glow` in tokens.css. */
export const GLOW_OPTIONS: readonly { value: GlowChoice; label: string; swatch: string | null }[] = [
  { value: 'teal', label: 'Teal', swatch: 'var(--bp-h-teal)' },
  { value: 'blue', label: 'Blue', swatch: 'var(--bp-link)' },
  { value: 'indigo', label: 'Indigo', swatch: 'var(--bp-focus-border)' },
  { value: 'violet', label: 'Violet', swatch: 'var(--bp-h-purple)' },
  { value: 'rose', label: 'Rose', swatch: 'var(--bp-h-pink)' },
  { value: 'amber', label: 'Amber', swatch: 'var(--bp-h-orange)' },
  { value: 'off', label: 'Off', swatch: null },
];

const THEME_KEY = 'biller.theme';
const GLOW_KEY = 'biller.glow';
const listeners = new Set<() => void>();

function isPreference(value: string | null): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark-sidebar' || value === 'dark';
}

function isGlow(value: string | null): value is GlowChoice {
  return GLOW_OPTIONS.some((option) => option.value === value);
}

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

let preference: ThemePreference = (() => {
  const stored = readStorage('local', THEME_KEY);
  return isPreference(stored) ? stored : 'system';
})();

let glow: GlowChoice = (() => {
  const stored = readStorage('local', GLOW_KEY);
  return isGlow(stored) ? stored : 'teal';
})();

function resolve(pref: ThemePreference): ResolvedTheme {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return pref === 'dark' ? 'dark' : 'light';
}

let resolved: ResolvedTheme = resolve(preference);

/** Reflects the choices onto <html> (mirrored by the boot script in index.html). */
function apply(): void {
  resolved = resolve(preference);
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  root.setAttribute('data-rail', preference === 'dark-sidebar' && resolved === 'light' ? 'dark' : 'light');
  root.setAttribute('data-glow', glow);
  for (const listener of listeners) listener();
}

export function initTheme(): void {
  apply();
  try {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (preference === 'system') apply();
    });
  } catch {
    /* matchMedia unavailable */
  }
}

export function setThemePreference(next: ThemePreference): void {
  preference = next;
  writeStorage('local', THEME_KEY, next);
  apply();
}

export function setGlowChoice(next: GlowChoice): void {
  glow = next;
  writeStorage('local', GLOW_KEY, next);
  apply();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

interface ThemeSnapshot {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  /** A dark rail on a light page. */
  darkRail: boolean;
  glow: GlowChoice;
}

let snapshot: ThemeSnapshot = { preference, resolved, darkRail: false, glow };

function getSnapshot(): ThemeSnapshot {
  const darkRail = preference === 'dark-sidebar' && resolved === 'light';
  if (
    snapshot.preference !== preference ||
    snapshot.resolved !== resolved ||
    snapshot.darkRail !== darkRail ||
    snapshot.glow !== glow
  ) {
    snapshot = { preference, resolved, darkRail, glow };
  }
  return snapshot;
}

export function useTheme(): ThemeSnapshot & {
  setPreference: (pref: ThemePreference) => void;
  setGlow: (choice: GlowChoice) => void;
} {
  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { ...current, setPreference: setThemePreference, setGlow: setGlowChoice };
}
